import {
  Create as CreateIcon,
  DomainVerification as DomainIcon,
  NewReleases as EndpointIcon,
  Title as TitleIcon,
  Router as PortIcon,
  NewReleases,
  Security as SecurityIcon,
  Shield as ShieldIcon,
  BugReport as VulnerabilityIcon,
  AssignmentInd as AssignmentIcon,
} from '@mui/icons-material';
import { Badge, Box, Typography, alpha } from '@mui/material';
import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTitleCount } from 'src/contexts/TitleCountContext';
import { useFfufCount } from 'src/contexts/FfufCountContext';
import { useLiveDomainCount } from 'src/contexts/LiveDomainCountContext';
import { useNucleiCount } from 'src/contexts/NucleiCountContext';
import { useAclBypassCount } from 'src/contexts/AclBypassCountContext';
import { useVulnerabilityFindingsCount } from 'src/contexts/VulnerabilityFindingsCountContext';
import { useVhostFindingsCount } from 'src/contexts/VhostFindingsCountContext';
import Iconify from 'src/components/iconify';
import { useTheme } from '@mui/material/styles';

// Section Header Component
const SectionHeader = ({ children }) => {
  const theme = useTheme();
  
  return (
    <Typography
      variant="overline"
      sx={{
        px: 2.5,
        py: 1.5,
        fontSize: '0.75rem',
        fontWeight: 'fontWeightBold',
        color: theme.palette.primary.main,
        letterSpacing: '0.12em',
        opacity: 0.9,
        textShadow: `0 0 8px ${alpha(theme.palette.primary.main, 0.4)}`,
        display: 'flex',
        alignItems: 'center',
        '&::before': {
          content: '""',
          width: '6px',
          height: '6px',
          borderRadius: '50%',
          backgroundColor: theme.palette.primary.main,
          marginRight: '8px',
          boxShadow: `0 0 8px ${alpha(theme.palette.primary.main, 0.8)}`,
        },
      }}
    >
      {children}
    </Typography>
  );
};

SectionHeader.propTypes = {
  children: PropTypes.node,
};

const NavItemWithBadge = ({ icon, count }) => {
  const theme = useTheme();
  
  if (!count) return (
    <Box sx={{ 
      width: '100%', 
      display: 'flex', 
      alignItems: 'center',
      '& .MuiSvgIcon-root': {
        transition: theme.transitions.create(['transform', 'color'], {
          duration: theme.transitions.duration.shorter,
        }),
      },
      ':hover .MuiSvgIcon-root': {
        color: theme.palette.primary.main,
        transform: 'scale(1.1)',
      },
    }}>
      {icon}
    </Box>
  );
  
  return (
    <Badge 
      badgeContent={count > 99 ? '99+' : count}
      sx={{ 
        '& .MuiSvgIcon-root': {
          order: 1,
          transition: theme.transitions.create(['transform', 'color'], {
            duration: theme.transitions.duration.shorter,
          }),
        },
        ':hover .MuiSvgIcon-root': {
          color: theme.palette.primary.main,
          transform: 'scale(1.1)',
        },
        '& .MuiBadge-badge': {
          order: 2,
          ml: 'auto',
          mr: 1,
          backgroundColor: alpha(theme.palette.primary.main, 0.2),
          color: theme.palette.primary.main,
          boxShadow: `0 0 0 1px ${alpha(theme.palette.primary.main, 0.3)}`,
          fontWeight: 'bold',
        }
      }}
    >
      {icon}
    </Badge>
  );
};

NavItemWithBadge.propTypes = {
  icon: PropTypes.node.isRequired,
  count: PropTypes.number,
};

// Custom styled search icon with a subtle background
const SearchNavIcon = ({ icon }) => {
  const theme = useTheme();
  
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        '& .MuiSvgIcon-root': {
          color: 'primary.main',
          transition: theme.transitions.create(['transform', 'color'], {
            duration: theme.transitions.duration.shorter,
          }),
        },
        ':hover .MuiSvgIcon-root': {
          transform: 'scale(1.1)',
          color: theme.palette.primary.light,
        },
      }}
    >
      {icon}
    </Box>
  );
};

SearchNavIcon.propTypes = {
  icon: PropTypes.node.isRequired,
};

export default function useNavConfig() {
  const { count: titleCount } = useTitleCount();
  const { count: ffufCount } = useFfufCount();
  const { count: liveDomainCount } = useLiveDomainCount();
  const { count: nucleiCount } = useNucleiCount();
  const { count: aclBypassCount } = useAclBypassCount();
  const { count: vulnerabilityCount } = useVulnerabilityFindingsCount();
  const { count: vhostFindingsCount } = useVhostFindingsCount();

  const navItems = useMemo(() => [
    // EXECUTIVE DASHBOARD
    {
      title: 'Executive Dashboard',
      path: '/overview',
      icon: <Iconify icon="mdi:view-dashboard" />,
    },
    {
      title: 'Command Center',
      path: '/command-center',
      icon: <Iconify icon="mdi:security" />,
      info: 'Security operations command center with prioritized actions'
    },
    
    // ORGANIZATION
    {
      type: 'section',
      title: 'Organization',
    },
    {
      title: 'Overview & Cluster Management',
      path: '/organisation-stats',
      icon: <Iconify icon="mdi:earth" />,
    },
    {
      title: 'Team Ownership',
      path: '/team-asset-ownership',
      icon: <AssignmentIcon />,
    },
    {
      title: 'Company Profile',
      path: '/organizational-context-config',
      icon: <Iconify icon="mdi:domain" />,
    },
    {
      title: 'Risk Configuration',
      path: '/risk-scoring-config',
      icon: <Iconify icon="mdi:head-cog-outline" />,
    },
    {
      title: 'Memory Bank',
      path: '/memory-bank',
      icon: <Iconify icon="mdi:brain" />,
    },
    {
      title: 'Data Integrations',
      path: '/integrations',
      icon: <Iconify icon="mdi:connection" />,
    },
    {
      title: 'Customer Onboarding',
      path: '/customer-onboarding',
      icon: <Iconify icon="mdi:account-plus" />,
    },
    
    // RESEARCH & INTELLIGENCE
    {
      type: 'section',
      title: 'Intelligence',
    },
    {
      title: 'Asset Explorer',
      path: '/host-stats', 
      icon: <Iconify icon="mdi:magnify" />,
    },
    {
      title: 'Technology Map',
      path: '/tag-search',
      icon: <Iconify icon="mdi:sitemap" />,
    },
    {
      title: 'Vulnerability Analysis',
      path: '/findings-import',
      icon: <Iconify icon="mdi:shield-search" />,
    },
    {
      title: 'AI Summaries',
      path: '/ai-summaries',
      icon: <Iconify icon="mdi:brain" />,
    },
    {
      title: 'Endpoint Leads Analysis',
      path: '/domain-leads',
      icon: <Iconify icon="mdi:trending-up" />,
    },
    {
      title: 'Regression Testing',
      path: '/regression-tracker',
      icon: <Iconify icon="mdi:test-tube" />,
    },
    
    // ADVANCED TOOLS (Collapsible)
    {
      type: 'section', 
      title: 'Advanced Tools',
      collapsible: true,
      defaultCollapsed: true,
    },
    
    // Live Monitoring & Streams
    {
      type: 'subsection',
      title: 'Live Monitoring',
      advanced: true,
    },
    {
      title: 'Domain Stream',
      path: '/domain-live-stream',
      icon: <DomainIcon />,
      count: liveDomainCount,
      advanced: true,
      subsection: 'Live Monitoring',
    },
    {
      title: 'Port Stream',
      path: '/port-domain-live-stream',
      icon: <PortIcon />,
      count: 0,
      advanced: true,
      subsection: 'Live Monitoring',
    },
    {
      title: 'VHost Stream',
      path: '/vhost-live-stream',
      icon: <Iconify icon="mdi:server-network" />,
      count: vhostFindingsCount,
      advanced: true,
      subsection: 'Live Monitoring',
    },
    {
      title: 'New Titles',
      path: '/new-titles',
      icon: <TitleIcon />,
      count: titleCount,
      advanced: true,
      subsection: 'Live Monitoring',
    },
    
    // Security Testing & Vulnerability Management
    {
      type: 'subsection',
      title: 'Security Testing',
      advanced: true,
    },
    {
      title: 'Vulnerability Findings',
      path: '/vulnerability-findings',
      icon: <VulnerabilityIcon />,
      count: vulnerabilityCount,
      advanced: true,
      subsection: 'Security Testing',
    },
    {
      title: 'Nuclei Scanner',
      path: '/nuclei-scan', 
      icon: <SecurityIcon />,
      count: nucleiCount,
      advanced: true,
      subsection: 'Security Testing',
    },
    {
      title: 'Secrets Detection',
      path: '/secrets-live-stream',
      icon: <Iconify icon="mdi:key-alert" />,
      count: 0,
      advanced: true,
      subsection: 'Security Testing',
    },
    {
      title: 'ACL Bypasses',
      path: '/acl-bypasses',
      icon: <ShieldIcon />,
      count: aclBypassCount,
      advanced: true,
      subsection: 'Security Testing',
    },
    
    // Content Discovery & Fuzzing
    {
      type: 'subsection',
      title: 'Content Discovery',
      advanced: true,
    },
    {
      title: 'Content Fuzzer',
      path: '/ffuf-input',
      icon: <CreateIcon />,
      advanced: true,
      subsection: 'Content Discovery',
    },
    {
      title: 'Endpoint Results',
      path: '/ffuf-output',
      icon: <NewReleases />,
      count: ffufCount,
      advanced: true,
      subsection: 'Content Discovery',
    },
    {
      title: 'New Endpoint Leads',
      path: '/new-endpoints',
      icon: <EndpointIcon />,
      count: 0,
      advanced: true,
      subsection: 'Content Discovery',
    },
    {
      title: 'Related Domains',
      path: '/related-domains',
      icon: <NewReleases />,
      count: 0,
      advanced: true,
      subsection: 'Content Discovery',
    },
  ], [titleCount, ffufCount, liveDomainCount, nucleiCount, aclBypassCount, vulnerabilityCount, vhostFindingsCount]);

  return navItems;
}
